/**
 * RTWO Indexer Engine — Query Service
 */

import { INDEXER_SERVICE_IDS } from '../domain-models';
import type { IndexQueryId, RTWOIndexQuery, RTWOIndexQueryType } from '../domain-models';
import type { IIndexQueryService, IndexQueryResult } from '../service-interfaces';
import type { IndexerRepositories } from '../repositories/indexer-repositories';
import type { StorageAdapterRegistry } from '../registry/adapter-registries';
import { IndexerInternalEventTypes } from '../events/indexer-event-bus';
import { BaseIndexerService, type IndexerServiceContext } from './base-indexer-service';

export class IndexQueryService extends BaseIndexerService implements IIndexQueryService {
  readonly serviceId = INDEXER_SERVICE_IDS.QUERY_SERVICE;
  readonly serviceName = 'Index Query Service';
  readonly description = 'Executes queries against index storage. No fake query results returned.';
  readonly integrations = ['Core Engine', 'Index Storage Binding', 'Explorer Platform (future)'];
  readonly requiresRPCSource = false;
  readonly requiresIndexStorage = true;

  constructor(
    context: IndexerServiceContext,
    private readonly repositories: IndexerRepositories,
    private readonly storageRegistry: StorageAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_index_storage';
  }

  async executeQuery(queryType: RTWOIndexQueryType, queryText: string): Promise<IndexQueryResult> {
    if (!this.storageRegistry.isConfigured()) {
      return {
        success: false,
        queryId: null,
        results: [],
        error:
          'Index Storage Not Configured — Cannot execute queries until index storage is configured.',
        isPlaceholder: true,
        source: 'Indexer Engine — Index Storage Not Configured',
      };
    }

    const adapterResult = await this.storageRegistry
      .getActiveAdapter()
      .executeQuery(queryType, queryText);

    if (adapterResult?.query) {
      this.repositories.queries.save(adapterResult.query);
      this.context.eventBus.emit(IndexerInternalEventTypes.QUERY_EXECUTED, {
        queryId: adapterResult.query.queryId,
        queryType,
      });
    }

    return {
      success: false,
      queryId: null,
      results: [],
      error:
        'Index Storage Not Configured — Cannot execute queries until index storage is configured.',
      isPlaceholder: true,
      source: 'Indexer Engine — Index Storage Not Configured',
    };
  }

  getAllQueries(): RTWOIndexQuery[] {
    if (!this.storageRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.queries.findAll();
  }

  getQuery(queryId: IndexQueryId): RTWOIndexQuery | null {
    if (!this.storageRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.queries.findById(queryId);
  }
}
